/**
 * Access Control Service
 * Handles fetching and checking user access to products
 */
import { getIdToken } from '../contexts/AuthContext';
import { buildApiUrl, getAuthHeaders } from '../config/api';

/**
 * Get current user's access to all products
 * @param {Function} addError - Error handler function (optional)
 * @returns {Promise<Object>} Object with email, isAdmin, and access map
 */
export const getUserAccess = async (addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl('/auth/my-access'), {
      method: 'GET',
      headers: getAuthHeaders(idToken)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch user access: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    const errorMsg = error.message || 'Error fetching user access';
    if (addError) addError(errorMsg, error.stack, 'Access Control API');
    console.error('Error fetching user access:', error);
    throw error;
  }
};

/**
 * Check if user has access to a specific product
 * @param {Object} accessMap - Access map from getUserAccess()
 * @param {string} productName - Product name to check
 * @returns {boolean} True if user has access
 */
export const hasAccess = (accessMap, productName) => {
  if (!accessMap || typeof accessMap !== 'object') {
    return false;
  }
  return accessMap[productName] === true;
};
